import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import {
  FetchApiKeys,
  FetchFundingSettings,
  FetchRejectionReasons,
  FetchUserNotificationsConfig,
} from 'src/constants/actionTypes'
import { getUserNotificationConfig } from 'src/selectors/user'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { EmailNotificationsItem } from './EmailNotificationsItem'
import { SettingsSidebarItem } from './SettingsSidebarItem'
import { GSIcon } from 'src/components/shared/GSIcon'
import {
  getCustomRejectionReasons,
  getDefaultRejectionReasons,
} from 'src/selectors/rejectionReason'
import { UserPermission, UserProductName } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from 'src/constants/routes'
import { getCustomerApiKeys } from 'src/selectors/apiKey'

const SettingsModalComponent = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const isNotifications = location.pathname.includes(ROUTES.SETTINGS_NOTIFICATIONS_URL)
  const isDeclineReasons = location.pathname.includes(ROUTES.SETTINGS_DECLINE_REASONS_URL)
  const isApiKeys = location.pathname.includes(ROUTES.SETTINGS_API_KEYS_URL)

  const { hasPermission, hasProduct } = useCheckPermissions()

  const userNotificationConfig = useSelector(getUserNotificationConfig)
  const defaultReasons = useSelector(getDefaultRejectionReasons)
  const customReasons = useSelector(getCustomRejectionReasons)
  const apiKeys = useSelector(getCustomerApiKeys)

  const container = useMemo(() => document.getElementById('root'), [])

  const showApiKeys = useMemo(
    () => hasProduct(UserProductName.ApiIntegration) && hasPermission(UserPermission.ApiKeysManage),
    [hasPermission, hasProduct],
  )

  useEffect(() => {
    dispatch(FetchUserNotificationsConfig.request())

    if (showApiKeys) {
      dispatch(FetchApiKeys.request())
    }

    if (hasPermission(UserPermission.CustomerReviewRejectionReasonView)) {
      dispatch(FetchRejectionReasons.request())
    }

    if (hasPermission(UserPermission.PaymentsView) && hasPermission(UserPermission.PaymentsEdit)) {
      dispatch(FetchFundingSettings.request())
    }
  }, [dispatch, hasPermission, showApiKeys])

  const handleClose = useCallback(() => {
    if (location.state?.background) {
      navigate(-1)
    } else {
      navigate(ROUTES.CONTRACTOR_ONBOARDINGS_URL)
    }
  }, [location, navigate])

  const handleSelectNotifications = useCallback(() => {
    navigate(ROUTES.SETTINGS_NOTIFICATIONS_URL, {
      state: { background: location?.state?.background },
    })
  }, [location, navigate])

  const handleSelectDeclineReasons = useCallback(() => {
    navigate(ROUTES.SETTINGS_DECLINE_REASONS_URL, {
      state: { background: location?.state?.background },
    })
  }, [location, navigate])

  const handleSelectApiKeys = useCallback(() => {
    navigate(ROUTES.SETTINGS_API_KEYS_URL, {
      state: { background: location?.state?.background },
    })
  }, [location, navigate])

  const apiKeysSubtitle = useMemo(() => {
    if (apiKeys.length === 0) {
      return 'No keys'
    }

    return `${apiKeys.length} active`
  }, [apiKeys.length])

  if (!userNotificationConfig) {
    return null
  }

  return (
    <Modal
      container={container}
      open
      onClose={handleClose}
      aria-labelledby="settings-modal-title"
      aria-describedby="settings-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4 z-[2000]`}
    >
      <div
        data-testid="customer-account-settings-modal"
        css={tw`bg-white rounded-lg overflow-hidden flex flex-col max-h-[720px] w-full max-w-[900px]`}
      >
        <ModalHeader
          title="Settings"
          onClose={handleClose}
          closeButtonDataTestId="customer-account-settings-close-btn"
        />
        <div css={tw`flex bg-[#F6F6F6] flex-1 min-h-0`}>
          <div css={tw`flex w-[359px] flex-col bg-white p-4 gap-4 overflow-y-auto`}>
            <div css={tw`flex flex-col gap-2`}>
              <Typography variant="h6" css={tw`font-semibold mb-2`}>
                Notifications
              </Typography>
              <EmailNotificationsItem
                dataTestId="customer-account-settings-notifications-item"
                isSelected={isNotifications}
                onClick={handleSelectNotifications}
              />
            </div>
            {hasPermission(UserPermission.CustomerReviewRejectionReasonView) && (
              <div css={tw`flex flex-col`}>
                <Typography variant="h6" css={tw`font-semibold mb-3`}>
                  Customizations
                </Typography>
                <SettingsSidebarItem
                  dataTestId="customer-account-settings-decline-reasons-item"
                  icon={<GSIcon iconName="gs_circle_x_1" width={20} height={20} />}
                  title="Decline reasons"
                  subtitle={`${customReasons.length} custom • ${defaultReasons.length} default`}
                  isSelected={isDeclineReasons}
                  onClick={handleSelectDeclineReasons}
                />
              </div>
            )}
            {showApiKeys && (
              <div css={tw`flex flex-col`}>
                <Typography variant="h6" css={tw`font-semibold mb-3`}>
                  Security
                </Typography>
                <SettingsSidebarItem
                  dataTestId="customer-account-settings-api-keys-item"
                  icon={<GSIcon iconName="gs_zap" width={20} height={20} />}
                  title="API keys"
                  subtitle={apiKeysSubtitle}
                  isSelected={isApiKeys}
                  onClick={handleSelectApiKeys}
                />
              </div>
            )}
          </div>

          <div css={tw`bg-[#EDEDED] flex w-[1px]`} />

          <div css={tw`flex flex-1 flex-col p-4 gap-2 relative overflow-y-auto`}>
            <Outlet />
          </div>
        </div>
      </div>
    </Modal>
  )
}

export const SettingsModal = memo(SettingsModalComponent)
